/* eslint-disable */
import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Chip,
  FormControl,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Stack,
  Pagination,
  SelectChangeEvent,
  ImageList,
  ImageListItem,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { BRANDS, ProductType } from "../types/types";
import { useNavigate, useSearchParams } from "react-router-dom";
import ProductCard from "../components/ProductCard";
import { Product } from "../types/product";
import { useProductStore } from "../store/productStore";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { slugify } from "../utils/generalFunction";

const ProductsPage = () => {
  const [brand, setBrand] = useState<string[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [capacity, setCapacity] = useState<string[]>([]);
  const [technology, setTechnology] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(24);
  const navigate = useNavigate();

  const capacities = [
    "1.0 HP",
    "1.5 HP",
    "2.0 HP",
    "2.5 HP",
    "3.0 HP",
    "3.5 HP",
    "4.0 HP",
    "4.5 HP",
    "5.0 HP",
    "5.5 HP",
  ];
  const productType = ProductType;
  const technologies = [
    "Có Inverter",
    "Không Inverter",
    "Inverter - Loại 1 chiều (chỉ làm lạnh)",
    "Inverter - Loại 2 chiều (Làm lạnh - Sưởi ấm)",
    "Mono - Loại 1 chiều (chỉ làm lạnh)",
  ];

  const { products } = useProductStore();

  // 1. Logic đồng bộ URL vào State (Chỉ chạy khi searchParams thay đổi)
  useEffect(() => {
    const categoryParam = searchParams.get("category");
    const brandParam = searchParams.get("brand");

    if (categoryParam) {
      const matched = productType.find(
        (item) => slugify(item) === categoryParam,
      );
      if (matched) {
        setCategories([matched]);
      }
    } else {
      setCategories([]);
    }

    if (brandParam) {
      setBrand([brandParam]);
    } else {
      setBrand([]);
    }
  }, [searchParams, productType]);

  // 2. Logic Lọc sản phẩm (Tính toán trực tiếp bằng useMemo)
  // Giải quyết vấn đề: filter đồng bộ ngay lập tức, không bị nhảy dữ liệu
  const filteredProducts = useMemo(() => {
    return products.filter((product) => {
      // Điều kiện 1: Brand (AND)
      const matchBrand =
        brand.length === 0 ||
        brand.some((b) => product.brandName?.toLowerCase() === b.toLowerCase());

      // Điều kiện 2: Categories (AND)
      const matchCategory =
        categories.length === 0 ||
        (product.productCategory &&
          categories.includes(product.productCategory));

      // Điều kiện 3: Capacity (AND)
      const matchCapacity =
        capacity.length === 0 ||
        (product.hp &&
          capacity.some((cap) =>
            product.hp?.toLowerCase().includes(cap.toLowerCase()),
          ));

      // Điều kiện 4: Technology (AND)
      const matchTechnology =
        technology.length === 0 ||
        (product.name &&
          technology.some((tech) =>
            product.name?.toLowerCase().includes(tech.toLowerCase()),
          ));

      // Phải thỏa mãn TẤT CẢ các bộ lọc đang chọn
      return matchBrand && matchCategory && matchCapacity && matchTechnology;
    });
  }, [products, brand, categories, capacity, technology]);

  // 3. Reset trang về 1 khi bất kỳ bộ lọc nào thay đổi
  useEffect(() => {
    setCurrentPage(1);
  }, [brand, categories, capacity, technology]);

  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);

  const currentItems = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredProducts.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredProducts, currentPage, itemsPerPage]);

  const [openFilterBrand, setOpenFilterBrand] = React.useState(false);
  const [openFilterCapacity, setOpenFilterCapacity] = React.useState(false);
  const [openFilterInverter, setOpenFilterInverter] = React.useState(false);
  const [openFilterCategory, setOpenFilterCategory] = React.useState(false);

  const handleOpenFilter = (type: string) => {
    if (type === "capacity") {
      setOpenFilterCapacity(true);
    } else if (type === "inverter") {
      setOpenFilterInverter(true);
    } else if (type === "category") {
      setOpenFilterCategory(true);
    } else {
      setOpenFilterBrand(true);
    }
  };

  const handleCloseFilter = (type: string) => {
    if (type === "capacity") {
      setOpenFilterCapacity(false);
    } else if (type === "inverter") {
      setOpenFilterInverter(false);
    } else if (type === "category") {
      setOpenFilterCategory(false);
    } else {
      setOpenFilterBrand(false);
    }
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number,
  ) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleItemsPerPageChange = (event: SelectChangeEvent<number>) => {
    const newItemsPerPage = Number(event.target.value);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  return (
    <Container className="!max-w-full px-3 md:px-10">
      <Box className="flex flex-col md:flex-row justify-start items-start gap-4 my-4">
        <Box className="flex justify-between items-center w-full md:w-auto">
          <Button
            onClick={() => handleOpenFilter("brand")}
            variant="outlined"
            endIcon={<KeyboardArrowDownIcon />}
            sx={{ textTransform: "none" }}
            className="w-full md:w-auto"
          >
            Thương hiệu
          </Button>
          <Dialog
            disableEscapeKeyDown
            open={openFilterBrand}
            onClose={() => handleCloseFilter("brand")}
          >
            <DialogTitle>Chọn thương hiệu</DialogTitle>
            <DialogContent>
              <Box component="form" sx={{ display: "flex", flexWrap: "wrap" }}>
                <FormControl component="fieldset">
                  <FormGroup aria-label="position" row className="">
                    {BRANDS.map((brandItem) => (
                      <Box
                        className={`flex items-center border m-2 px-2 rounded-lg cursor-pointer hover:shadow ${
                          brand.includes(brandItem.id) ? "border-red-500" : ""
                        }`}
                        key={brandItem.id}
                        onClick={() => {
                          if (!brand.includes(brandItem.id)) {
                            setBrand([...brand, brandItem.id]);
                          } else {
                            setBrand(brand.filter((id) => id !== brandItem.id));
                          }
                        }}
                      >
                        <FormControlLabel
                          key={brandItem.id}
                          value={brandItem.id}
                          control={<Checkbox />}
                          label={""}
                          checked={brand.includes(brandItem.id)}
                        />
                        <img
                          src={"../" + brandItem.logo}
                          alt={brandItem.name}
                          className="max-w-14 h-auto"
                        />
                      </Box>
                    ))}
                  </FormGroup>
                </FormControl>
              </Box>
            </DialogContent>
            <DialogActions className="!justify-center">
              <Button onClick={() => handleCloseFilter("brand")}>Cancel</Button>
              <Button onClick={() => handleCloseFilter("brand")}>Ok</Button>
            </DialogActions>
          </Dialog>
        </Box>

        <Box className="flex justify-between items-center w-full md:w-auto">
          <Button
            variant="outlined"
            endIcon={<KeyboardArrowDownIcon />}
            onClick={() => handleOpenFilter("capacity")}
            sx={{ textTransform: "none" }}
            className="w-full md:w-auto"
          >
            Công suất làm lạnh
          </Button>
          <Dialog
            disableEscapeKeyDown
            open={openFilterCapacity}
            onClose={() => handleCloseFilter("capacity")}
          >
            <DialogTitle>Chọn công suất làm lạnh</DialogTitle>
            <DialogContent>
              <Box component="form" sx={{ display: "flex", flexWrap: "wrap" }}>
                <FormControl component="fieldset">
                  <FormGroup aria-label="position" row>
                    {capacities.map((cap) => (
                      <FormControlLabel
                        key={cap}
                        value={cap}
                        control={
                          <Checkbox
                            onClick={() => {
                              if (!capacity.includes(cap)) {
                                setCapacity([...capacity, cap]);
                              } else {
                                setCapacity(
                                  capacity.filter((item) => item !== cap),
                                );
                              }
                            }}
                          />
                        }
                        label={cap}
                        checked={capacity.includes(cap)}
                      />
                    ))}
                  </FormGroup>
                </FormControl>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => handleCloseFilter("capacity")}>
                Cancel
              </Button>
              <Button onClick={() => handleCloseFilter("capacity")}>Ok</Button>
            </DialogActions>
          </Dialog>
        </Box>

        <Box className="flex justify-between items-center w-full md:w-auto">
          <Button
            variant="outlined"
            endIcon={<KeyboardArrowDownIcon />}
            onClick={() => handleOpenFilter("category")}
            sx={{ textTransform: "none" }}
            className="w-full md:w-auto"
          >
            Kiểu dáng
          </Button>
          <Dialog
            disableEscapeKeyDown
            open={openFilterCategory}
            onClose={() => handleCloseFilter("category")}
          >
            <DialogTitle>Chọn kiểu dáng máy lạnh</DialogTitle>
            <DialogContent>
              <Box component="form" sx={{ display: "flex", flexWrap: "wrap" }}>
                <FormControl component="fieldset">
                  <FormGroup aria-label="position" row>
                    {productType.map((type) => (
                      <FormControlLabel
                        key={type}
                        value={type}
                        control={
                          <Checkbox
                            onClick={() => {
                              if (!categories.includes(type)) {
                                setCategories([...categories, type]);
                              } else {
                                setCategories(
                                  categories.filter((item) => item !== type),
                                );
                              }
                            }}
                          />
                        }
                        label={type}
                        checked={categories.includes(type)}
                      />
                    ))}
                  </FormGroup>
                </FormControl>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => handleCloseFilter("category")}>
                Cancel
              </Button>
              <Button onClick={() => handleCloseFilter("category")}>Ok</Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Box>

      {/* Active Filters Display */}
      {(brand.length > 0 ||
        capacity.length > 0 ||
        technology.length > 0 ||
        categories.length > 0) && (
        <Box className="bg-blue-50 p-2 rounded-lg">
          <Typography
            variant="subtitle2"
            className="font-semibold mb-2 text-blue-800"
          >
            Bộ lọc đang áp dụng:
          </Typography>
          <Box className="flex flex-wrap gap-2">
            {brand.length > 0 && (
              <Chip
                label={`Thương hiệu: ${brand}`}
                size="small"
                color="primary"
                variant="filled"
                onDelete={() => {
                  const newParams = new URLSearchParams(searchParams);
                  newParams.delete("brand");
                  setSearchParams(newParams);
                  setBrand([]);
                }}
              />
            )}
            {capacity.length > 0 && (
              <Chip
                label={`Công suất: ${capacity}`}
                size="small"
                color="primary"
                variant="filled"
                onDelete={() => setCapacity([])}
              />
            )}
            {technology.length > 0 && (
              <Chip
                label={`Công nghệ: ${technology}`}
                size="small"
                color="primary"
                variant="filled"
                onDelete={() => setTechnology([])}
              />
            )}
            {categories.length > 0 && (
              <Chip
                label={`Kiểu dáng: ${categories}`}
                size="small"
                color="primary"
                variant="filled"
                onDelete={() => {
                  const newParams = new URLSearchParams(searchParams);
                  newParams.delete("category");
                  setSearchParams(newParams);
                  setCategories([]);
                }}
              />
            )}
          </Box>
        </Box>
      )}

      <ImageList
        className="w-full !py-10"
        variant="masonry"
        sx={{
          columnCount: {
            xs: "2 !important",
            sm: "4 !important",
            md: "5 !important",
          },
          columnGap: {
            xs: "8px !important",
            sm: "12px !important",
            md: "16px !important",
          },
          rowGap: {
            xs: "8px !important",
            sm: "12px !important",
            md: "16px !important",
          },
        }}
      >
        {currentItems.map((item) => (
          <ImageListItem key={item.id}>
            <ProductCard
              product={item}
              onClick={() => {
                navigate(`/product-detail/${item.id}`);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
            />
          </ImageListItem>
        ))}
      </ImageList>

      <Box className="flex flex-col md:flex-row justify-between items-center">
        <Box className="flex items-center gap-4">
          <Box>
            Hiển thị {(currentPage - 1) * itemsPerPage + 1} -{" "}
            {Math.min(currentPage * itemsPerPage, filteredProducts.length)} /{" "}
            {filteredProducts.length} sản phẩm
          </Box>

          <FormControl sx={{ minWidth: 120 }} size="small">
            <InputLabel>Số lượng</InputLabel>
            <Select
              value={itemsPerPage}
              label="Số lượng"
              onChange={handleItemsPerPageChange}
            >
              <MenuItem value={12}>12</MenuItem>
              <MenuItem value={24}>24</MenuItem>
              <MenuItem value={36}>36</MenuItem>
              <MenuItem value={48}>48</MenuItem>
            </Select>
          </FormControl>
        </Box>
        {filteredProducts.length > 0 && (
          <Stack spacing={2} alignItems="center" sx={{ mt: 4, mb: 4 }}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              variant="outlined"
              size="small"
              showFirstButton
              showLastButton
            />
          </Stack>
        )}
      </Box>

      {filteredProducts.length === 0 && (
        <Box sx={{ textAlign: "center", padding: "40px", color: "gray" }}>
          Không tìm thấy sản phẩm nào phù hợp
        </Box>
      )}
    </Container>
  );
};

export default ProductsPage;
